import { type JSXElementConstructor, type ReactElement, useEffect, useState } from 'react'

import EditIcon from '@mui/icons-material/Edit'
import type { DialogLike } from '@/components/ConsoleDialog/handlers/dialogHandlers.types'
import { useUnmountingDelay } from '@/hooks/useUnmountingDelay'
import { warnDev } from '@/utils/warnDev'

import { ActionMenuItem } from '../ActionMenuItem/ActionMenuItem'
import { useActionMenuContext } from '../context/ActionMenuContext'
import { MenuItemLike } from '../index.types'

type Props<Data extends Record<string, unknown>> = MenuItemLike & {
  getForm: (
    row: Data
  ) =>
    | ReactElement<DialogLike, JSXElementConstructor<DialogLike>>
    | Promise<ReactElement<DialogLike, JSXElementConstructor<DialogLike>>>
  isActive?: ((row: Data) => boolean) | boolean
  icon?: ReactElement
}

export const ActionFormItem = <Data extends Record<string, unknown>>({
  getForm,
  displaySeparately,
  overrideText,
  isActive = true,
  icon
}: Props<Data>) => {
  const { close, open, isMounted, isOpen } = useUnmountingDelay()
  const { row, closeMenu } = useActionMenuContext<Data>()
  const [FormDialog, setFormDialog] = useState<ReactElement<
    DialogLike,
    JSXElementConstructor<DialogLike>
  > | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!isOpen) return

    const loadForm = async () => {
      setIsLoading(true)
      try {
        const form = await Promise.resolve(getForm(row))
        setFormDialog(form)
      } catch (error) {
        warnDev(`Error loading form: ${error}`)
        setFormDialog(null)
      } finally {
        setIsLoading(false)
      }
    }

    loadForm()
  }, [getForm, row, isOpen])

  if (
    (typeof isActive === 'function' && !isActive(row)) ||
    (typeof isActive === 'boolean' && !isActive)
  ) {
    return null
  }

  return (
    <>
      <ActionMenuItem
        text={overrideText || 'Edit'}
        onClick={open}
        icon={icon || <EditIcon />}
        displaySeparately={displaySeparately}
      />
      {isMounted && !isLoading && FormDialog && (
        <FormDialog.type
          key={FormDialog.key}
          {...FormDialog.props}
          open={isOpen}
          onClose={() => {
            FormDialog.props?.onClose?.()
            close()
            closeMenu?.()
            setFormDialog(null)
          }}
        />
      )}
    </>
  )
}
